import { useCallback, useState } from "react"

import { useTranslation } from "@l2r-front/l2r-i18n"
import { Roadwork, Task } from "@l2r-front/l2r-proptypes"
import { useAlertsDispatchContext } from "@l2r-front/l2r-query"

import { I18N_NAMESPACE } from "../../../../common/constants/i18n"
import {
    RoadworkTaskComponent,
    RoadworkTaskError,
    RoadworkTaskSkeleton,
} from "../../components/RoadworkTask"
import { useRoadworksStateContext } from "../../contexts/RoadworksContext"
import { RoadworkTaskEditFormPost } from "../../forms/RoadworkTaskForm/RoadworkTaskEditForm.post"
import { useTaskDelete } from "../../hooks/mutations/useTaskMutation"
import { useTaskType } from "../../hooks/queries/useTaskType"

import { RoadworkTaskEditModal } from "../RoadworkTaskEditModal/RoadworkTaskEditModal"

type RoadworkTaskProps = {
    closed?: boolean
    onDeleteTask: () => void
    index: number
    isDeletable?: boolean
    isEditable?: boolean
    task: Task,
    roadwork: Roadwork,
}

export function RoadworkTask(props: RoadworkTaskProps) {

    const {
        index,
        isDeletable = false,
        isEditable = false,
        task,
        roadwork,
    } = props

    const [editModalOpen, setEditModalOpen] = useState(false)
    const { currency } = useRoadworksStateContext()
    const { setAlert } = useAlertsDispatchContext()
    const { t } = useTranslation(I18N_NAMESPACE)

    const {
        data: taskType,
        isLoading: isLoadingTaskType,
        isError: isErrorTaskType,
    } = useTaskType(task?.taskType, { enabled: !!task?.taskType })

    const deleteMutation = useTaskDelete({
        errorSnackbarMessage: t(I18N_NAMESPACE, "components.roadworkDetailsForm.snackbar.error"),
        successSnackbarMessage: t(I18N_NAMESPACE, "components.roadworkDetailsForm.snackbar.success"),
    })

    const onDelete = useCallback(() => {
        setAlert({
            alertTitle: t(I18N_NAMESPACE, "containers.roadworkTask.alert.title"),
            callback: () => deleteMutation.mutate({ uuid: task.uuid } as unknown as void),
            open: true,
        })
    }, [deleteMutation, task, setAlert, t])

    if (isErrorTaskType) {
        return <RoadworkTaskError />
    }

    if (isLoadingTaskType || !currency || typeof currency === 'string') {
        return <RoadworkTaskSkeleton />
    }

    return <>
        <RoadworkTaskComponent
            currency={currency}
            editionEnabled={isEditable}
            id={`task-${task.uuid || index}`}
            isDeletable={isDeletable}
            onDelete={onDelete}
            onEdit={() => setEditModalOpen(true)}
            task={task}
            taskType={taskType}
        />
        {editModalOpen && <RoadworkTaskEditModal
            open={editModalOpen}
            mode="update"
            onClose={() => setEditModalOpen(false)}
            FormComponent={RoadworkTaskEditFormPost}
            task={task}
            roadwork={roadwork}
        />}
    </>
}
